import { Briefcase, GraduationCap, Search, TrendingUp, Users } from 'lucide-react';

export type DemoRole = 'student' | 'rep' | 'professor' | 'manager' | 'recruiter';

export interface DemoRoleDef {
  id: DemoRole;
  label: string;
  tagline: string;
  /** What the demo drops you into. */
  landing: string;
  highlights: string[];
  icon: React.ComponentType<{ className?: string }>;
}

export const DEMO_ROLES: DemoRoleDef[] = [
  {
    id: 'student',
    label: 'Student',
    tagline: 'Practice AI role-plays, see your score climb and get interview-ready.',
    landing: '/dashboard?tab=practice',
    highlights: ['AI role-plays', 'Graded feedback', 'Career tools'],
    icon: GraduationCap,
  },
  {
    id: 'rep',
    label: 'Sales Rep',
    tagline: 'Run reps before real calls — discovery, objections, cold calls, negotiation.',
    landing: '/dashboard?tab=practice',
    highlights: ['Cold calls', 'Objection drills', 'Score progression'],
    icon: TrendingUp,
  },
  {
    id: 'professor',
    label: 'Professor',
    tagline: 'Assign scenarios, track a class and grade with a consistent rubric.',
    landing: '/dashboard?tab=students',
    highlights: ['Class roster', 'Assignments', 'Rubric analytics'],
    icon: Users,
  },
  {
    id: 'manager',
    label: 'Sales Manager',
    tagline: 'Coach your team with evidence: transcripts, rubrics and trends.',
    landing: '/dashboard?tab=students',
    highlights: ['Team dashboard', 'Call reviews', 'Coaching insights'],
    icon: Briefcase,
  },
  {
    id: 'recruiter',
    label: 'Recruiter',
    tagline: 'Find sales talent by demonstrated skill, not just a resume.',
    landing: '/dashboard?tab=leaderboards',
    highlights: ['Talent rankings', 'Verified scores', 'Job postings'],
    icon: Search,
  },
];

const KEY = 'sellaris.demo.role.v1';

export function setDemoRole(role: DemoRole) {
  try {
    localStorage.setItem(KEY, role);
    // Entering a demo always replays the walkthrough for that persona.
    Object.keys(localStorage)
      .filter(k => k.startsWith('sellaris.tour.seen'))
      .forEach(k => localStorage.removeItem(k));
    sessionStorage.removeItem('sellaris.tour.autostart.done');
  } catch {
    /* ignore */
  }
}


export function getDemoRole(): DemoRole | null {
  try {
    const v = localStorage.getItem(KEY);
    return DEMO_ROLES.some(r => r.id === v) ? (v as DemoRole) : null;
  } catch {
    return null;
  }
}

export function getDemoRoleDef(role: DemoRole | null): DemoRoleDef | null {
  return DEMO_ROLES.find(r => r.id === role) ?? null;
}

/** Role/user_type the shared demo workspace should present for each persona. */
const ROLE_PROFILE_SHAPE: Record<
  DemoRole,
  { role: 'student' | 'professor' | 'recruiter'; user_type: string }
> = {
  student: { role: 'student', user_type: 'student' },
  rep: { role: 'student', user_type: 'sales_rep' },
  professor: { role: 'professor', user_type: 'professor' },
  // Managers need the staff-side surfaces (rep analytics, rosters) — the
  // enterprise wording comes from user_type: 'manager'.
  manager: { role: 'professor', user_type: 'manager' },
  recruiter: { role: 'recruiter', user_type: 'professional' },
};

/**
 * Everyone shares one demo account, so the visible role has to come from the
 * persona picked on /try. Without this a student demo still shows professor
 * and recruiter surfaces.
 */
export function applyDemoRoleShape<T extends { role?: unknown; user_type?: unknown }>(profile: T): T {
  const role = getDemoRole();
  if (!role) return profile;
  const shape = ROLE_PROFILE_SHAPE[role];
  return { ...profile, role: shape.role, user_type: shape.user_type };
}

/** Demo personas must never unlock admin-only surfaces. */
export function demoRoleAllowsAdmin(): boolean {
  return false;
}
